class AnalyticsService {
  constructor() {
    this.isEnabled = false;
    this.trackingId = null;
    this.init();
  }

  init() {
    // Only initialize analytics if GTM container ID is provided (Mission Pest Control only)
    this.trackingId = process.env.VUE_APP_GTM_CONTAINER_ID;
    
    if (this.trackingId && this.trackingId.trim() !== '') {
      this.loadGoogleTagManager(this.trackingId);
      this.isEnabled = true;
      console.log('GTM Analytics initialized for container ID:', this.trackingId);
    } else {
      console.log('Analytics disabled - no GTM container ID provided');
    }
  }

  loadGoogleTagManager(containerId) {
    // Initialize GTM dataLayer
    window.dataLayer = window.dataLayer || [];
    
    // Create and inject GTM script
    const script1 = document.createElement('script');
    script1.async = true;
    script1.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
    document.head.appendChild(script1);

    // Initialize GTM
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js'
    });
  }

  // Track registration form step completion
  trackFormStep(stepName, additionalData = {}) {
    if (!this.isEnabled) return;

    const stepNumber = this.getStepNumber(stepName);
    
    // Filter out any personal data before sending to analytics
    const sanitizedData = this.sanitizeData(additionalData);
    
    window.dataLayer.push({
      event: 'registration_step_completed',
      step_name: stepName,
      step_number: stepNumber,
      event_category: 'registration_form',
      event_label: `Step ${stepNumber}: ${stepName}`,
      ...sanitizedData
    });

    console.log('GTM event pushed:', {
      event: 'registration_step_completed',
      step_name: stepName,
      step_number: stepNumber,
      ...sanitizedData
    });
  }

  // Track when user starts the registration process
  trackRegistrationStart() {
    if (!this.isEnabled) return;

    window.dataLayer.push({
      event: 'registration_started',
      event_category: 'registration_form',
      event_label: 'User started registration process'
    });
  }

  // Track successful registration completion
  trackRegistrationComplete(customerData = {}) {
    if (!this.isEnabled) return;

    const sanitizedData = this.sanitizeData(customerData);

    window.dataLayer.push({
      event: 'registration_completed',
      event_category: 'registration_form',
      event_label: 'User completed full registration',
      value: sanitizedData.billingAmount || null,
      ...sanitizedData
    });
  }


  // Helper method to get step numbers
  getStepNumber(stepName) {
    const stepMap = {
      'address_selection': 1,
      'customer_creation': 2,
      'appointment_scheduling': 3,
      'billing_setup': 4,
      'registration_complete': 5
    };
    
    return stepMap[stepName] || 0;
  }

  // Track custom events for specific actions
  trackCustomEvent(eventName, parameters = {}) {
    if (!this.isEnabled) return;

    const sanitizedParams = this.sanitizeData(parameters);

    window.dataLayer.push({
      event: eventName,
      event_category: 'registration_form',
      ...sanitizedParams
    });
  }

  // Data sanitization to remove personal information
  sanitizeData(data) {
    if (!data || typeof data !== 'object') return data;

    const sanitized = {};
    const personalDataFields = [
      'name', 'firstName', 'lastName', 'email', 'phone', 'phoneNumber',
      'address', 'street', 'city', 'zip', 'zipCode', 'postalCode',
      'formatted_address', 'place_id', 'lat', 'lng', 'latitude', 'longitude',
      'cardNumber', 'ccv', 'expirationDate', 'billingAddress',
      'customer', 'customerData', 'appointment', 'billing'
    ];

    for (const [key, value] of Object.entries(data)) {
      const keyLower = key.toLowerCase();
      
      // Skip personal data fields
      if (personalDataFields.some(field => keyLower.includes(field.toLowerCase()))) {
        continue;
      }

      // Allow safe analytics data
      if (this.isAllowedAnalyticsField(key, value)) {
        sanitized[key] = value;
      }
    }

    return sanitized;
  }

  // Define allowed fields for analytics
  isAllowedAnalyticsField(key, value) {
    const allowedFields = [
      'step_name', 'step_number', 'event_category', 'event_label',
      'billingAmount', 'planType', 'frequency', 'routeCount', 
      'appointmentCount', 'pestCount', 'hasExistingAccount',
      'registrationSource', 'deviceType', 'browserType'
    ];

    // Allow if field is in allowed list
    if (allowedFields.includes(key)) return true;

    // Allow numeric values that don't contain personal data
    if (typeof value === 'number' && !key.toLowerCase().includes('phone')) return true;

    // Allow boolean values
    if (typeof value === 'boolean') return true;

    // Allow string values that are clearly not personal (short, generic values)
    if (typeof value === 'string' && value.length < 50 && 
        !this.containsPersonalData(value)) return true;

    return false;
  }

  // Check if a string might contain personal data
  containsPersonalData(str) {
    const personalPatterns = [
      /@/, // email
      /\d{3}-\d{3}-\d{4}/, // phone
      /\d{5}/, // zip code
      /\d+\s+\w+\s+(st|street|ave|avenue|rd|road|blvd|boulevard|dr|drive|ln|lane|ct|court)/i // address
    ];

    return personalPatterns.some(pattern => pattern.test(str));
  }
}

// Export singleton instance
export default new AnalyticsService();